// Budget-Verwaltung = Control Panel für regelmässige Ausgaben (ADR 0042):
// zeigt nur noch wiederkehrende Ausgaben als Karten, gruppiert nach
// "Regelmässige Ausgaben" und "Löhne & Sozialabgaben". Lohn-Karten öffnen das
// Team-Profil, Versicherungs-Karten das Versicherungs-Modal; alle anderen sind
// direkt per "Ändern ab..." (Betrag/Anzahl ab Datum, Kette) oder Detail-Formular
// bearbeitbar. Das Formular dient weiterhin als eingebetteter Editor
// (showFormDirectly) für Cashflow und Controlling.
import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  getBudgetEntries,
  saveBudgetEntry,
  deleteBudgetEntry,
  getKonten,
  moveOverdueExpensesToNextKW,
  changeRecurringEntryFromDate,
  recalculateInsuranceBudget,
  recalculateFAKBudget,
  calculateAverageMonthlyRecurringExpenses,
  getCashflowWarnschwelle,
  saveCashflowWarnschwelle,
} from "../services/azureBudgetService";
import {
  calculateMwstAmounts,
  getQuartalFromDate,
  calculateQuartalsMwst,
} from "../services/mwstService";
import { getProjectPhases } from "../services/azureProjectPhasesService";
import { getAllProjects } from "../services/azureProjectsService";
import { getAllPartners, createPartner } from "../services/azurePartnersService";
import { getProjectPartners, addProjectPartner } from "../services/azureRelationsService";
import { uploadBookings } from "../services/azureBookingService";
import { getAllTeamMembers } from "../services/azureTeamService";
import { getAllActiveRates } from "../services/azureInsuranceService";
import TeamDetailModal from "./TeamDetailModal";
import InsuranceSettingsModal from "./InsuranceSettingsModal";

// Leeres Formular (mehrfach für Resets verwendet)
const EMPTY_FORM_DATA = {
  typ: "Ausgabe",
  name: "",
  konto: "",
  typ_timing: "Einmalig",
  ab_wann: "",
  bis_wann: "",
  haeufigkeit: "monatlich",
  wann: "",
  betrag: "",
  anzahl: "",
  stueckbetrag: "",
  projekt: "",
  phase: "",
  partner_id: "",
  partner_name: "",
  mwst_satz: 8.1,
  ist_brutto: true,
  ist_prognose: false,
  versand_datum: "",
  zahlungsfrist: 30,
  automatic_payment: false,
  status: "",
};

const HAEUFIGKEIT_KURZ = {
  monatlich: "Monat",
  quartalsweise: "Quartal",
  "jährlich": "Jahr",
};

const BudgetVerwaltungMwSt = ({
  onDataChange,
  showFormDirectly = false,
  presetData = null,
  onClose = null,
  readOnly = false,
}) => {
  const [budgetEntries, setBudgetEntries] = useState([]);
  const [editingEntry, setEditingEntry] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [konten, setKonten] = useState([]);
  const [projekte, setProjekte] = useState([]);
  const [projectPhases, setProjectPhases] = useState([]);
  const [loading, setLoading] = useState(false);
  const [nextMwstPayment, setNextMwstPayment] = useState(null);
  const [allPartners, setAllPartners] = useState([]);
  const [projectPartnersList, setProjectPartnersList] = useState([]);
  const [partnerSuggestions, setPartnerSuggestions] = useState([]);
  const [showPartnerSuggestions, setShowPartnerSuggestions] = useState(false);
  const [showPartnerRoleModal, setShowPartnerRoleModal] = useState(false);
  const [pendingPartner, setPendingPartner] = useState(null);

  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  // Control Panel: Quick-Edit auf einer Karte ("Ändern ab...")
  const [quickEdit, setQuickEdit] = useState(null); // { entryId, anzahl, stueckbetrag, abDatum }

  // Cashflow-Warnschwelle: gespeicherter fixer Wert (null = Standard 3 x Ø)
  const [warnschwelle, setWarnschwelle] = useState(null);
  const [warnschwelleInput, setWarnschwelleInput] = useState("");
  const [warnschwelleSaving, setWarnschwelleSaving] = useState(false);

  // Team-Profil / Versicherungs-Modal (für team-gesteuerte Karten)
  const [teamModalMember, setTeamModalMember] = useState(null);
  const [showInsuranceModal, setShowInsuranceModal] = useState(false);
  const teamMembersRef = useRef(null); // Cache: einmal geladen pro Sitzung

  // Erweiterte Formular-Daten mit MwSt
  const [formData, setFormData] = useState({ ...EMPTY_FORM_DATA });

  // Berechnete MwSt-Werte für Anzeige
  const [mwstCalculation, setMwstCalculation] = useState({
    bruttoBetrag: 0,
    nettoBetrag: 0,
    mwstBetrag: 0,
    mwstSatz: 8.1,
  });

  // Berechnetes Fälligkeitsdatum (works for both Einnahme and Ausgabe)
  const calculatedFaelligkeitsdatum = useMemo(() => {
    if (
      formData.versand_datum &&
      formData.zahlungsfrist &&
      formData.typ_timing === "Einmalig"
    ) {
      const sendDate = new Date(formData.versand_datum);
      const dueDate = new Date(sendDate);
      dueDate.setDate(dueDate.getDate() + parseInt(formData.zahlungsfrist));
      return dueDate.toISOString().split("T")[0];
    }
    return formData.wann || "";
  }, [
    formData.versand_datum,
    formData.zahlungsfrist,
    formData.typ_timing,
    formData.wann,
  ]);

  useEffect(() => {
    loadInitialData();
  }, []);

  useEffect(() => {
    const betragNum = parseFloat(formData.betrag) || 0;
    if (betragNum > 0) {
      const calculation = calculateMwstAmounts(
        betragNum,
        parseFloat(formData.mwst_satz) || 8.1,
        formData.ist_brutto
      );
      setMwstCalculation(calculation);
    } else {
      setMwstCalculation({
        bruttoBetrag: 0,
        nettoBetrag: 0,
        mwstBetrag: 0,
        mwstSatz: parseFloat(formData.mwst_satz),
      });
    }
  }, [formData.betrag, formData.mwst_satz, formData.ist_brutto]);

  // Load project phases when project is selected
  useEffect(() => {
    const loadPhases = async () => {
      if (formData.projekt) {
        try {
          const phases = await getProjectPhases(formData.projekt);
          setProjectPhases(phases);
        } catch (error) {
          console.error("Error loading project phases:", error);
          setProjectPhases([]);
        }
      } else {
        setProjectPhases([]);
        // Clear phase if project is cleared
        if (formData.phase) {
          setFormData(prev => ({ ...prev, phase: "" }));
        }
      }
    };
    loadPhases();
  }, [formData.projekt]);

  useEffect(() => {
    if (showFormDirectly) {
      if (presetData) {
        // Wenn nur eine ID übergeben wurde, lade den vollständigen Eintrag
        if (presetData.id && !presetData.name) {
          const fullEntry = budgetEntries.find(entry => entry.id === presetData.id);
          if (fullEntry) {
            handleEdit(fullEntry);
          }
        } else {
          setFormData({
            ...formData,
            ...presetData,
            suggestions: undefined, // nicht ins formData
          });

          // Wenn presetData eine id hat, setze editingEntry für Update
          if (presetData.id) {
            setEditingEntry({ id: presetData.id });
          }

          // Load project partners if project is preset
          if (presetData.projekt) {
            loadProjectPartnersAsync(presetData.projekt);
          }

          setShowForm(true);
        }
      } else {
        // Wenn presetData null ist, öffne das Formular für einen neuen Eintrag
        setShowForm(true);
      }
    }
  }, [showFormDirectly, presetData, budgetEntries]);

  // ===== Control Panel: Karten-Daten =====
  // Nur laufende/zukünftige regelmässige Ausgaben; 13. Monatslöhne werden nicht
  // separat gezeigt (lohn_eintrag_id *_13, sie laufen über das Team-Profil).
  // Team-gesteuerte Einträge (Löhne, Sozialabgaben) bilden die zweite Gruppe.
  const recurringCards = useMemo(() => {
    const todayStr = new Date().toISOString().split("T")[0];
    const relevant = budgetEntries.filter(
      (e) =>
        e.typ === "Ausgabe" &&
        e.typ_timing === "Regelmässig" &&
        !(e.lohn_eintrag_id || "").endsWith("_13") &&
        (!e.bis_wann || e.bis_wann >= todayStr)
    );
    const isPersonal = (e) => Boolean(e.team_member_id && e.lohn_eintrag_id);
    const byName = (a, b) => (a.name || "").localeCompare(b.name || "", "de");
    return {
      allgemein: relevant.filter((e) => !isPersonal(e)).sort(byName),
      personal: relevant.filter(isPersonal).sort(byName),
    };
  }, [budgetEntries]);

  // Ø regelmässige Monatsausgaben (Jahresbetrag / 12, inkl. 13. Monatslohn) —
  // wird immer angezeigt; Standard-Warnschwelle für den Cashflow-Graph = 3 x
  const avgMonthlyExpenses = useMemo(
    () => calculateAverageMonthlyRecurringExpenses(budgetEntries),
    [budgetEntries]
  );
  const defaultWarnschwelle = Math.round(avgMonthlyExpenses * 3 * 100) / 100;
  const effektiveWarnschwelle = warnschwelle ?? defaultWarnschwelle;

  const handleSaveWarnschwelle = async () => {
    try {
      setWarnschwelleSaving(true);
      const saved = await saveCashflowWarnschwelle(warnschwelleInput);
      setWarnschwelle(saved);
      setWarnschwelleInput(saved != null ? String(saved) : "");
      if (onDataChange) onDataChange();
    } catch (error) {
      console.error("Fehler beim Speichern der Warnschwelle:", error);
      alert("Fehler beim Speichern der Warnschwelle.");
    } finally {
      setWarnschwelleSaving(false);
    }
  };

  const handleResetWarnschwelle = async () => {
    try {
      setWarnschwelleSaving(true);
      await saveCashflowWarnschwelle(null);
      setWarnschwelle(null);
      setWarnschwelleInput("");
      if (onDataChange) onDataChange();
    } catch (error) {
      console.error("Fehler beim Zurücksetzen der Warnschwelle:", error);
      alert("Fehler beim Zurücksetzen der Warnschwelle.");
    } finally {
      setWarnschwelleSaving(false);
    }
  };

  // Quick-Edit ("Ändern ab..."): Anzahl x Stückbetrag ab Datum — der bisherige
  // Eintrag läuft bis zum Vortag, ab dem Datum übernimmt ein neuer (Kette)
  const openQuickEdit = (entry) => {
    const brutto = entry.brutto_betrag || entry.betrag || 0;
    const anzahl = entry.anzahl && entry.anzahl > 0 ? entry.anzahl : 1;
    const stueck =
      entry.stueckbetrag && entry.stueckbetrag > 0
        ? entry.stueckbetrag
        : Math.round((brutto / anzahl) * 100) / 100;
    setQuickEdit({
      entryId: entry.id,
      anzahl: String(anzahl),
      stueckbetrag: String(stueck),
      abDatum: new Date().toISOString().split("T")[0],
    });
  };

  const handleQuickEditSave = async (entry) => {
    const anzahlNum = parseInt(quickEdit.anzahl) || 1;
    const stueckNum = parseFloat(quickEdit.stueckbetrag) || 0;
    const total = Math.round(anzahlNum * stueckNum * 100) / 100;
    if (!quickEdit.abDatum || total <= 0) {
      alert("Bitte gültigen Betrag und ein Datum angeben.");
      return;
    }
    try {
      setLoading(true);
      await changeRecurringEntryFromDate(entry, {
        abDatum: quickEdit.abDatum,
        betrag: total,
        anzahl: anzahlNum,
        stueckbetrag: stueckNum,
      });
      setBudgetEntries(await getBudgetEntries());
      setQuickEdit(null);
      if (onDataChange) onDataChange();
    } catch (error) {
      console.error("Fehler beim Ändern der regelmässigen Ausgabe:", error);
      alert("Fehler beim Speichern der Änderung.");
    } finally {
      setLoading(false);
    }
  };

  // Lohn-Karten: öffnen das Team-Profil des jeweiligen Mitglieds
  const handleOpenTeamMember = async (employeeUuid) => {
    try {
      if (!teamMembersRef.current) {
        teamMembersRef.current = await getAllTeamMembers();
      }
      const member = teamMembersRef.current.find(
        (m) => m.employeeUuid === employeeUuid
      );
      if (!member) {
        alert("Team-Mitglied nicht gefunden.");
        return;
      }
      setTeamModalMember(member);
    } catch (error) {
      console.error("Team-Daten konnten nicht geladen werden:", error);
      alert("Team-Daten konnten nicht geladen werden.");
    }
  };

  const handleTeamModalClose = async () => {
    setTeamModalMember(null);
    teamMembersRef.current = null; // Mitglied könnte geändert worden sein
    setBudgetEntries(await getBudgetEntries()); // Lohn-Budgets evtl. geändert
    if (onDataChange) onDataChange();
  };

  // Versicherungs-Karten: nach Satz-Änderung Aggregate neu rechnen + neu laden
  const handleRatesChanged = () => {
    getAllActiveRates(new Date().toISOString().split("T")[0])
      .then(async (rates) => {
        if (!rates) return;
        await Promise.all([
          recalculateInsuranceBudget(rates),
          recalculateFAKBudget(rates.fak),
        ]);
        setBudgetEntries(await getBudgetEntries());
        if (onDataChange) onDataChange();
      })
      .catch(() => {});
  };

  const loadInitialData = async () => {
    setLoading(true);
    try {
      // Verschiebe überfällige Ausgaben zur nächsten KW (vor dem Laden)
      await moveOverdueExpensesToNextKW();

      const [budgetData, kontenData, projekteData, partnersData, storedWarnschwelle] =
        await Promise.all([
          getBudgetEntries(),
          getKonten(),
          loadProjekte(),
          getAllPartners(),
          getCashflowWarnschwelle(),
        ]);

      setBudgetEntries(budgetData);
      setKonten(kontenData);
      setProjekte(projekteData);
      setAllPartners(partnersData);
      setWarnschwelle(storedWarnschwelle);
      setWarnschwelleInput(storedWarnschwelle != null ? String(storedWarnschwelle) : "");

      await loadNextMwstPayment(budgetData);
    } catch (error) {
      console.error("Fehler beim Laden der Budget-Daten:", error);
    } finally {
      setLoading(false);
    }
  };

  const loadNextMwstPayment = async (budgetData) => {
    try {
      const today = new Date();
      const currentQuarter = getQuartalFromDate(today);

      const currentMwst = calculateQuartalsMwst(budgetData, currentQuarter);

      if (currentMwst.saldo > 0) {
        setNextMwstPayment({
          id: `mwst-${currentQuarter}`,
          typ: "Ausgabe",
          name: `MwSt-Abrechnung ${currentQuarter}`,
          konto: "2200",
          typ_timing: "Einmalig",
          wann: currentMwst.faelligAm.toISOString().split("T")[0],
          betrag: currentMwst.saldo,
          projekt: "",
          kategorie: "Steuern",
          ist_mwst_abrechnung: true,
          quartal: currentQuarter,
        });
      } else {
        setNextMwstPayment(null);
      }
    } catch (error) {
      console.error("Fehler beim Berechnen der MwSt-Abrechnung:", error);
      setNextMwstPayment(null);
    }
  };

  const loadProjekte = async () => {
    try {
      const projects = await getAllProjects();

      return projects
        .filter((project) => project.code) // Only projects with a code
        .map((project) => ({
          id: project.code,
          name: project.code
            ? `${project.code} - ${project.title || "Unbekannt"}`
            : project.title || "Unbekannt",
        }))
        .sort((a, b) => a.id.localeCompare(b.id));
    } catch (error) {
      console.warn("Konnte Projekte nicht laden:", error);
      return [];
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newValue = type === "checkbox" ? checked : value;

    setFormData((prev) => {
      const updated = {
        ...prev,
        [name]: newValue,
      };

      // Clear phase and load project partners when project changes
      if (name === "projekt") {
        updated.phase = "";
        updated.partner_id = "";
        updated.partner_name = "";
        // Load project partners asynchronously
        if (value) {
          loadProjectPartnersAsync(value);
        } else {
          setProjectPartnersList([]);
        }
      }

      // Auto-detect project code from name field
      if (name === "name") {
        const projectCodeMatch = value.match(/\b(\d{3})\b/); // Match 3-digit codes like 014, 020
        if (projectCodeMatch) {
          const detectedCode = projectCodeMatch[1];
          // Check if this code exists in our projects list
          const matchingProject = projekte.find(p => p.id === detectedCode);
          if (matchingProject && !prev.projekt) {
            // Only auto-assign if projekt field is empty
            updated.projekt = detectedCode;
          }
        }
      }

      // Sync wann with versand_datum for backward compatibility
      if (
        name === "versand_datum" &&
        prev.typ_timing === "Einmalig"
      ) {
        if (value) {
          updated.wann = value; // backward compat — versand_datum is the primary date
        }
      }

      // Anzahl x Stückbetrag <-> Gesamtbetrag synchron halten (regelmässige
      // Ausgaben): Anzahl/Stückbetrag-Edits führen den Gesamtbetrag nach,
      // ein direkter Gesamtbetrag-Edit passt den Stückbetrag an (Anzahl bleibt)
      if (name === "anzahl" || name === "stueckbetrag") {
        const anzahlNum = parseInt(name === "anzahl" ? value : prev.anzahl) || 0;
        const stueckNum =
          parseFloat(name === "stueckbetrag" ? value : prev.stueckbetrag) || 0;
        if (anzahlNum > 0 && stueckNum > 0) {
          updated.betrag = String(Math.round(anzahlNum * stueckNum * 100) / 100);
        }
      }
      if (name === "betrag") {
        const anzahlNum = parseInt(prev.anzahl) || 0;
        const betragNum = parseFloat(value) || 0;
        if (anzahlNum > 0 && betragNum > 0) {
          updated.stueckbetrag = String(
            Math.round((betragNum / anzahlNum) * 100) / 100
          );
        }
      }

      return updated;
    });
  };

  // Load project partners for current project
  const loadProjectPartnersAsync = async (projectId, partnersList = null) => {
    try {
      const relations = await getProjectPartners(projectId);
      // Get partner details for each relation
      // Relations have: targetId (partner ID), relationType (role), function (description)
      // Use provided partnersList or fall back to allPartners from state
      const availablePartners = partnersList || allPartners;
      const partnersWithDetails = relations
        .filter(r => r.targetId)
        .map(r => {
          const partner = availablePartners.find(p => String(p.id) === String(r.targetId));
          // Always include the partner ID from the relation, even if we don't have full details
          // This ensures the "already linked" check works correctly
          return {
            id: r.targetId,
            name: partner?.name || `Partner ${r.targetId}`,
            role: r.relationType || r.function || "Partner",
            ...(partner || {})
          };
        });
      setProjectPartnersList(partnersWithDetails);
    } catch (error) {
      console.error("Error loading project partners:", error);
      setProjectPartnersList([]);
    }
  };

  // Handle partner name input with suggestions
  const handlePartnerNameChange = (e) => {
    const value = e.target.value;
    setFormData(prev => ({
      ...prev,
      partner_name: value,
      partner_id: "", // Clear ID when typing new name
    }));

    if (value.length >= 2) {
      // First show project partners that match
      const projectMatches = projectPartnersList.filter(p =>
        p.name?.toLowerCase().includes(value.toLowerCase())
      );
      // Then show all partners that match (but not already in project)
      const otherMatches = allPartners.filter(p =>
        p.name?.toLowerCase().includes(value.toLowerCase()) &&
        !projectPartnersList.find(pp => String(pp.id) === String(p.id))
      );
      setPartnerSuggestions([...projectMatches, ...otherMatches].slice(0, 10));
      setShowPartnerSuggestions(true);
    } else {
      setPartnerSuggestions([]);
      setShowPartnerSuggestions(false);
    }
  };

  // Select a partner from suggestions
  const handleSelectPartner = (partner) => {
    // Check if partner is already linked to project (handle string/number ID comparison)
    const isProjectPartner = projectPartnersList.find(p => String(p.id) === String(partner.id));

    if (isProjectPartner) {
      // Already linked to project, just use it
      setFormData(prev => ({
        ...prev,
        partner_id: partner.id,
        partner_name: partner.name,
        // Update name to "{project-code} - {partner}"
        name: prev.projekt ? `${prev.projekt} - ${partner.name}` : prev.name,
      }));
      setShowPartnerSuggestions(false);
    } else {
      // Partner exists in CRM but not linked to project - ask for role
      setPendingPartner(partner);
      setShowPartnerRoleModal(true);
      setShowPartnerSuggestions(false);
    }
  };

  // Handle partner role selection and link to project
  const handlePartnerRoleConfirm = async (role) => {
    if (!pendingPartner || !formData.projekt) return;

    try {
      // Add partner to project
      await addProjectPartner(formData.projekt, pendingPartner.id, role);

      // Reload project partners
      await loadProjectPartnersAsync(formData.projekt);

      // Set the partner in form and update name to "{project-code} - {partner}"
      setFormData(prev => ({
        ...prev,
        partner_id: pendingPartner.id,
        partner_name: pendingPartner.name,
        name: prev.projekt ? `${prev.projekt} - ${pendingPartner.name}` : prev.name,
      }));
    } catch (error) {
      console.error("Error linking partner to project:", error);
      alert("Fehler beim Verknüpfen des Partners mit dem Projekt");
    }

    setShowPartnerRoleModal(false);
    setPendingPartner(null);
  };

  // Create new partner if not found
  const handleCreateNewPartner = async () => {
    if (!formData.partner_name || !formData.projekt) return;

    try {
      // Create new partner in CRM (minimal data)
      const newPartner = await createPartner({
        name: formData.partner_name,
        type: "Supplier",
      });

      // Ask for role to link to project
      setPendingPartner(newPartner);
      setShowPartnerRoleModal(true);
      setShowPartnerSuggestions(false);

      // Update allPartners list
      setAllPartners(prev => [...prev, newPartner]);
    } catch (error) {
      console.error("Error creating partner:", error);
      alert("Fehler beim Erstellen des Partners");
    }
  };

  const handleSubmit = async () => {
    if (!formData.name || !formData.betrag) {
      alert("Bitte füllen Sie alle Pflichtfelder aus.");
      return;
    }

    if (formData.typ_timing === "Einmalig" && !formData.versand_datum && !formData.wann) {
      alert("Bitte geben Sie ein Datum an.");
      return;
    }

    if (formData.typ_timing === "Regelmässig" && !formData.ab_wann) {
      alert("Bitte geben Sie ein Startdatum an.");
      return;
    }

    try {
      setLoading(true);

      // Gesamtbetrag ist massgeblich (handleInputChange hält Anzahl/Stückbetrag
      // und Gesamtbetrag bereits synchron — der zuletzt editierte Wert gewinnt)
      // editingEntry zuerst spreaden: Felder, die das Formular nicht kennt
      // (beleg_id, sent_date, paid_date, invoice_reference, source, ...),
      // dürfen beim Bearbeiten nicht verloren gehen.
      const savedEntry = await saveBudgetEntry({
        ...(editingEntry || {}),
        ...formData,
        id: editingEntry?.id,
      });

      if (editingEntry) {
        setBudgetEntries((prev) =>
          prev.map((entry) =>
            entry.id === editingEntry.id ? savedEntry : entry
          )
        );
      } else {
        setBudgetEntries((prev) => [...prev, savedEntry]);
      }

      setFormData({ ...EMPTY_FORM_DATA });

      setEditingEntry(null);
      setShowForm(false);
      setProjectPartnersList([]);

      if (onDataChange) {
        onDataChange();
      }

      // NEU: onSave callback aufrufen
      if (onClose) {
        await onClose(); // Rufe onClose auf (was loadData + close macht)
      }
    } catch (error) {
      console.error("Fehler beim Speichern:", error);
      alert("Fehler beim Speichern des Eintrags.");
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (entry) => {
    // Für alte Einträge ohne versand_datum/zahlungsfrist: berechne versand_datum basierend auf wann - 30 Tage
    let versandDatum = entry.versand_datum || "";
    let zahlungsfrist = entry.zahlungsfrist || 30;

    // wann IS the invoice/bill date (same as versand_datum)
    // For old entries without versand_datum, use wann directly
    if (
      !entry.versand_datum &&
      entry.wann &&
      entry.typ === "Einnahme" &&
      entry.typ_timing === "Einmalig"
    ) {
      versandDatum = entry.wann;
    }

    // Load project partners if entry has a project
    if (entry.projekt) {
      loadProjectPartnersAsync(entry.projekt);
    }

    setFormData({
      typ: entry.typ,
      name: entry.name,
      konto: entry.konto,
      typ_timing: entry.typ_timing,
      ab_wann: entry.ab_wann,
      bis_wann: entry.bis_wann,
      haeufigkeit: entry.haeufigkeit,
      wann: entry.wann,
      betrag: entry.ist_brutto
        ? entry.brutto_betrag.toString()
        : entry.netto_betrag.toString(),
      anzahl: entry.anzahl ? String(entry.anzahl) : "",
      stueckbetrag: entry.stueckbetrag ? String(entry.stueckbetrag) : "",
      projekt: entry.projekt || "",
      phase: entry.phase || "",
      partner_id: entry.partner_id || "",
      partner_name: entry.partner_name || "",
      mwst_satz: entry.mwst_satz !== undefined ? entry.mwst_satz : 8.1,
      ist_brutto: entry.ist_brutto !== false,
      ist_prognose: entry.ist_prognose !== false,
      versand_datum: versandDatum,
      zahlungsfrist: zahlungsfrist,
      automatic_payment: entry.automatic_payment || false,
      status: entry.status || "",
    });
    setEditingEntry(entry);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    try {
      setLoading(true);
      await deleteBudgetEntry(id);
      setBudgetEntries((prev) => prev.filter((entry) => entry.id !== id));

      if (onDataChange) {
        onDataChange();
      }

      // Close modal and reset form after deleting
      setEditingEntry(null);
      setShowForm(false);
      setFormData({ ...EMPTY_FORM_DATA });
      setProjectPartnersList([]);

      if (onClose) {
        await onClose();
      }
    } catch (error) {
      console.error("Fehler beim Löschen:", error);
      alert("Fehler beim Löschen des Eintrags.");
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setFormData({ ...EMPTY_FORM_DATA });
    setEditingEntry(null);
    setShowForm(false);
    setProjectPhases([]);
    setProjectPartnersList([]);

    if (onClose) {
      onClose();
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("de-CH", {
      style: "currency",
      currency: "CHF",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  const formatDatum = (iso) =>
    iso ? new Date(iso).toLocaleDateString("de-CH") : "";

  // Eine Karte pro regelmässiger Ausgabe. Lohn-Karten öffnen das Team-Profil,
  // Versicherungs-Karten das Versicherungs-Modal; alle anderen sind direkt per
  // Quick-Edit ("Ändern ab...") oder Detail-Formular bearbeitbar.
  const renderExpenseCard = (entry) => {
    const isInsurance =
      entry.team_member_id === "insurance_aggregated" ||
      entry.team_member_id === "fak_aggregated";
    const isLohn =
      Boolean(entry.team_member_id && entry.lohn_eintrag_id) && !isInsurance;
    const brutto = entry.brutto_betrag || entry.betrag || 0;
    // Anzahl x Einzelpreis immer zeigen, wenn erfasst (z.B. "3 x CHF 23.40")
    const hatStueck = entry.anzahl > 0 && entry.stueckbetrag > 0;
    const todayStr = new Date().toISOString().split("T")[0];
    const startetSpaeter = entry.ab_wann && entry.ab_wann > todayStr;
    const editingThis = quickEdit?.entryId === entry.id;
    const quickTotal = editingThis
      ? Math.round(
          (parseInt(quickEdit.anzahl) || 1) *
            (parseFloat(quickEdit.stueckbetrag) || 0) *
            100
        ) / 100
      : 0;

    return (
      <div
        key={entry.id}
        className={`budget-card${isLohn ? " budget-card--team" : ""}${
          isInsurance ? " budget-card--versicherung" : ""
        }`}
      >
        <div className="budget-card__head">
          <span className="budget-card__name" title={entry.name}>
            {entry.name}
          </span>
          {entry.konto && (
            <span className="budget-card__konto">{entry.konto}</span>
          )}
        </div>

        <div className="budget-card__betrag">
          {formatCurrency(brutto)}
          <span className="budget-card__rhythmus">
            /{HAEUFIGKEIT_KURZ[entry.haeufigkeit] || entry.haeufigkeit}
          </span>
        </div>
        {hatStueck && (
          <div className="budget-card__stueck">
            {entry.anzahl} × {formatCurrency(entry.stueckbetrag)}
          </div>
        )}

        <div className="budget-card__meta">
          <span className="budget-card__zeitraum">
            {formatDatum(entry.ab_wann)} –{" "}
            {entry.bis_wann ? formatDatum(entry.bis_wann) : "unbegrenzt"}
          </span>
          {startetSpaeter && (
            <span className="budget-card__badge zukunft">
              ab {formatDatum(entry.ab_wann)}
            </span>
          )}
          {entry.automatic_payment && (
            <span className="budget-card__badge">Automatisch</span>
          )}
          {entry.ist_prognose && (
            <span className="budget-card__badge">Prognose</span>
          )}
        </div>

        {!readOnly && (
          <div className="budget-card__actions">
            {isLohn ? (
              <button
                className="budget-card__btn"
                onClick={() => handleOpenTeamMember(entry.team_member_id)}
                disabled={loading}
              >
                Im Team-Profil bearbeiten
              </button>
            ) : isInsurance ? (
              <button
                className="budget-card__btn"
                onClick={() => setShowInsuranceModal(true)}
                disabled={loading}
              >
                Versicherungssätze bearbeiten
              </button>
            ) : (
              <>
                <button
                  className="budget-card__btn budget-card__btn--primary"
                  onClick={() =>
                    editingThis ? setQuickEdit(null) : openQuickEdit(entry)
                  }
                  disabled={loading}
                >
                  Ändern ab...
                </button>
                <button
                  className="budget-card__btn"
                  onClick={() => handleEdit(entry)}
                  disabled={loading}
                >
                  Details
                </button>
              </>
            )}
          </div>
        )}

        {editingThis && !readOnly && (
          <div className="budget-card__quickform">
            <div className="quickform-fields">
              <label>
                Anzahl
                <input
                  type="number"
                  min="1"
                  step="1"
                  value={quickEdit.anzahl}
                  onChange={(e) =>
                    setQuickEdit({ ...quickEdit, anzahl: e.target.value })
                  }
                />
              </label>
              <label>
                Betrag/Einheit
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={quickEdit.stueckbetrag}
                  onChange={(e) =>
                    setQuickEdit({ ...quickEdit, stueckbetrag: e.target.value })
                  }
                />
              </label>
              <label>
                gilt ab
                <input
                  type="date"
                  value={quickEdit.abDatum}
                  onChange={(e) =>
                    setQuickEdit({ ...quickEdit, abDatum: e.target.value })
                  }
                />
              </label>
            </div>
            <div className="quickform-foot">
              <span className="quickform-total">
                Neu: {formatCurrency(quickTotal)}/
                {HAEUFIGKEIT_KURZ[entry.haeufigkeit] || entry.haeufigkeit}
              </span>
              <button
                className="budget-card__btn budget-card__btn--primary"
                onClick={() => handleQuickEditSave(entry)}
                disabled={loading}
              >
                Übernehmen
              </button>
              <button
                className="budget-card__btn"
                onClick={() => setQuickEdit(null)}
                disabled={loading}
              >
                Abbrechen
              </button>
            </div>
            <div className="quickform-hint">
              Der bisherige Betrag läuft bis am Vortag; ab dem gewählten Datum
              gilt der neue.
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="budget-verwaltung">
      <div className="budget-header">
        <h2>Regelmässige Ausgaben</h2>
        <div className="budget-header-actions">
          <button
            className="btn btn-secondary"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading || loading}
          >
            {uploading ? "Importiere..." : "Excel Journal hochladen"}
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept=".xls,.xlsx"
            onChange={async (e) => {
              const file = e.target.files[0];
              if (!file) return;
              setUploading(true);
              try {
                await uploadBookings(file);
                if (onDataChange) onDataChange();
                e.target.value = "";
              } catch (error) {
                console.error("Upload error:", error);
                alert("Fehler beim Upload: " + error.message);
              } finally {
                setUploading(false);
              }
            }}
            style={{ display: "none" }}
          />
          <button
            onClick={() => {
              // Control Panel zeigt regelmässige Ausgaben — Formular passend vorbelegen
              setFormData({
                ...EMPTY_FORM_DATA,
                typ: "Ausgabe",
                typ_timing: "Regelmässig",
              });
              setShowForm(true);
            }}
            className="btn btn-primary"
            disabled={loading}
          >
            + Neue regelmässige Ausgabe
          </button>
        </div>
      </div>

      {/* Formular - UNVERÄNDERT */}
      {showForm && (
        <div className="budget-form-overlay">
          <div className="budget-form">
            <h3>
              {editingEntry ? "Position bearbeiten" : "Neue Budget-Position"}
            </h3>

            <div className="form-grid">
              <div className="form-group">
                <label>Typ *</label>
                <select
                  name="typ"
                  value={formData.typ}
                  onChange={handleInputChange}
                  required
                >
                  <option value="Einnahme">Einnahme</option>
                  <option value="Ausgabe">Ausgabe</option>
                </select>
              </div>

              <div className="form-group">
                <label>Name *</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="Bezeichnung der Budget-Position"
                  required
                />
              </div>

              <div className="form-group">
                <label>Konto</label>
                <select
                  name="konto"
                  value={formData.konto}
                  onChange={handleInputChange}
                >
                  <option value="">Konto wählen</option>
                  {konten.map((konto) => (
                    <option key={konto.id} value={konto.id}>
                      {konto.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Timing *</label>
                <select
                  name="typ_timing"
                  value={formData.typ_timing}
                  onChange={handleInputChange}
                  required
                >
                  <option value="Einmalig">Einmalig</option>
                  <option value="Regelmässig">Regelmässig</option>
                </select>
              </div>

              {formData.typ === "Ausgabe" &&
                formData.typ_timing === "Regelmässig" && (
                  <div className="form-group">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        name="automatic_payment"
                        checked={formData.automatic_payment || false}
                        onChange={handleInputChange}
                      />
                      Automatische Zahlung (Status-Tracking überspringen)
                    </label>
                  </div>
                )}

              {/* Anzahl x Stückbetrag (z.B. Abos: 3 x Adobe à 120.-) — der
                  Gesamtbetrag wird automatisch nachgeführt */}
              {formData.typ === "Ausgabe" &&
                formData.typ_timing === "Regelmässig" && (
                  <>
                    <div className="form-group">
                      <label>Anzahl (optional)</label>
                      <input
                        type="number"
                        name="anzahl"
                        value={formData.anzahl}
                        onChange={handleInputChange}
                        placeholder="z.B. 3"
                        step="1"
                        min="1"
                      />
                    </div>
                    <div className="form-group">
                      <label>Betrag pro Einheit (CHF)</label>
                      <input
                        type="number"
                        name="stueckbetrag"
                        value={formData.stueckbetrag}
                        onChange={handleInputChange}
                        placeholder="0.00"
                        step="0.01"
                        min="0"
                      />
                    </div>
                  </>
                )}

              <div className="form-group">
                <label>MwSt-Satz</label>
                <select
                  name="mwst_satz"
                  value={formData.mwst_satz}
                  onChange={handleInputChange}
                >
                  <option value="0">0% (steuerfrei)</option>
                  <option value="8.1">8.1% (Standard)</option>
                </select>
              </div>

              <div className="form-group betrag-group">
                <label>Betrag (CHF) *</label>
                <div className="betrag-input-group">
                  <input
                    type="number"
                    name="betrag"
                    value={formData.betrag}
                    onChange={handleInputChange}
                    placeholder="0.00"
                    step="0.01"
                    min="0"
                    required
                  />
                  <div className="mwst-toggle">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        name="ist_brutto"
                        checked={formData.ist_brutto}
                        onChange={handleInputChange}
                      />
                      inkl. MwSt
                    </label>
                  </div>
                </div>
              </div>

              {formData.mwst_satz > 0 && formData.betrag && (
                <div className="form-group mwst-calculation">
                  <label>MwSt-Berechnung</label>
                  <div className="calculation-display">
                    <div className="calc-row">
                      <span>Netto: </span>
                      <span>{formatCurrency(mwstCalculation.nettoBetrag)}</span>
                    </div>
                    <div className="calc-row">
                      <span>MwSt ({mwstCalculation.mwstSatz}%): </span>
                      <span>{formatCurrency(mwstCalculation.mwstBetrag)}</span>
                    </div>
                    <div className="calc-row total">
                      <span>Brutto: </span>
                      <span>
                        {formatCurrency(mwstCalculation.bruttoBetrag)}
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {formData.typ_timing === "Einmalig" &&
                formData.typ === "Einnahme" && (
                  <>
                    <div className="form-group">
                      <label>Versanddatum *</label>
                      <input
                        type="date"
                        name="versand_datum"
                        value={formData.versand_datum}
                        onChange={handleInputChange}
                        required={
                          formData.typ_timing === "Einmalig" &&
                          formData.typ === "Einnahme"
                        }
                      />
                    </div>

                    <div className="form-group">
                      <label>Zahlungsfrist *</label>
                      <select
                        name="zahlungsfrist"
                        value={formData.zahlungsfrist}
                        onChange={handleInputChange}
                        required={
                          formData.typ_timing === "Einmalig" &&
                          formData.typ === "Einnahme"
                        }
                      >
                        <option value="10">10 Tage</option>
                        <option value="30">30 Tage</option>
                        <option value="45">45 Tage</option>
                      </select>
                    </div>

                    <div className="form-group">
                      <label>Fälligkeitsdatum (berechnet)</label>
                      <input
                        type="date"
                        value={calculatedFaelligkeitsdatum}
                        readOnly
                        disabled
                        style={{
                          backgroundColor: "var(--text-bg-color)",
                          cursor: "not-allowed",
                        }}
                      />
                    </div>
                  </>
                )}

              {formData.typ_timing === "Einmalig" &&
                formData.typ === "Ausgabe" && (
                  <>
                    <div className="form-group">
                      <label>Rechnungsdatum (optional)</label>
                      <input
                        type="date"
                        name="versand_datum"
                        value={formData.versand_datum}
                        onChange={handleInputChange}
                      />
                    </div>

                    <div className="form-group">
                      <label>Zahlungsfrist</label>
                      <select
                        name="zahlungsfrist"
                        value={formData.zahlungsfrist}
                        onChange={handleInputChange}
                      >
                        <option value="10">10 Tage</option>
                        <option value="30">30 Tage</option>
                        <option value="45">45 Tage</option>
                      </select>
                    </div>

                    <div className="form-group">
                      <label>Fälligkeitsdatum {formData.versand_datum ? "(berechnet)" : "*"}</label>
                      <input
                        type="date"
                        name="wann"
                        value={formData.versand_datum ? calculatedFaelligkeitsdatum : formData.wann}
                        onChange={handleInputChange}
                        required={formData.typ_timing === "Einmalig"}
                        readOnly={!!formData.versand_datum}
                        disabled={!!formData.versand_datum}
                        style={formData.versand_datum ? {
                          backgroundColor: "var(--text-bg-color)",
                          cursor: "not-allowed",
                        } : {}}
                      />
                    </div>
                  </>
                )}

              {presetData?.suggestions && presetData.suggestions.length > 0 && (
                <div className="payment-suggestions">
                  <label>Vorschläge aus Buchhaltung:</label>
                  <div className="suggestions-list">
                    {presetData.suggestions.map((sug, idx) => (
                      <button
                        key={idx}
                        type="button"
                        className="btn btn-secondary btn-sm"
                        onClick={() => {
                          setFormData((prev) => ({
                            ...prev,
                            betrag: sug.betrag.toString(),
                            wann: sug.valuta,
                          }));
                        }}
                      >
                        CHF {sug.betrag.toFixed(2)} -{" "}
                        {new Date(sug.valuta).toLocaleDateString("de-CH")}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {formData.typ_timing === "Regelmässig" && (
                <>
                  <div className="form-group">
                    <label>Häufigkeit</label>
                    <select
                      name="haeufigkeit"
                      value={formData.haeufigkeit}
                      onChange={handleInputChange}
                    >
                      <option value="monatlich">Monatlich</option>
                      <option value="quartalsweise">Quartalsweise</option>
                      <option value="jährlich">Jährlich</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Von *</label>
                    <input
                      type="date"
                      name="ab_wann"
                      value={formData.ab_wann}
                      onChange={handleInputChange}
                      required={formData.typ_timing === "Regelmässig"}
                    />
                  </div>

                  <div className="form-group">
                    <label>Bis (optional)</label>
                    <input
                      type="date"
                      name="bis_wann"
                      value={formData.bis_wann}
                      onChange={handleInputChange}
                    />
                  </div>
                </>
              )}

              <div className="form-group">
                <label>Projekt (optional)</label>
                <select
                  name="projekt"
                  value={formData.projekt}
                  onChange={handleInputChange}
                >
                  <option value="">Kein Projekt</option>
                  {projekte.map((projekt) => (
                    <option key={projekt.id} value={projekt.id}>
                      {projekt.name}
                    </option>
                  ))}
                </select>
              </div>

              {formData.projekt && projectPhases.length > 0 && (
                <div className="form-group">
                  <label>Phase</label>
                  <select
                    name="phase"
                    value={formData.phase || ""}
                    onChange={handleInputChange}
                  >
                    <option value="">-- Automatisch --</option>
                    {projectPhases.map((phase) => (
                      <option key={phase.id} value={phase.id}>
                        {phase.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <div className="form-group">
                <label>Status</label>
                <select
                  name="status"
                  value={formData.status}
                  onChange={handleInputChange}
                >
                  {formData.typ === "Einnahme" ? (
                    <>
                      <option value="">-- Automatisch --</option>
                      <option value="expected">Erwartet</option>
                      <option value="sent">Versendet</option>
                      <option value="paid">Bezahlt</option>
                      <option value="overdue">Überfällig</option>
                    </>
                  ) : (
                    <>
                      <option value="">-- Automatisch --</option>
                      <option value="expecting">Erwartet</option>
                      <option value="received">Erhalten</option>
                      <option value="paid">Bezahlt</option>
                      <option value="overdue">Überfällig</option>
                    </>
                  )}
                </select>
              </div>

              {formData.projekt && formData.typ === "Ausgabe" && (
                <div className="form-group partner-field">
                  <label>Partner / Lieferant (optional)</label>
                  <div className="partner-input-wrapper">
                    <input
                      type="text"
                      name="partner_name"
                      value={formData.partner_name}
                      onChange={handlePartnerNameChange}
                      onFocus={() => formData.partner_name.length >= 2 && setShowPartnerSuggestions(true)}
                      onBlur={() => setTimeout(() => setShowPartnerSuggestions(false), 200)}
                      placeholder="Partner suchen oder neu eingeben..."
                      autoComplete="off"
                    />
                    {formData.partner_id && (
                      <span className="partner-linked-badge">✓ Verknüpft</span>
                    )}
                  </div>
                  {showPartnerSuggestions && partnerSuggestions.length > 0 && (
                    <div className="partner-suggestions">
                      {partnerSuggestions.map((partner) => {
                        const isProjectPartner = projectPartnersList.find(p => String(p.id) === String(partner.id));
                        return (
                          <div
                            key={partner.id}
                            className={`partner-suggestion-item ${isProjectPartner ? "project-partner" : ""}`}
                            onClick={() => handleSelectPartner(partner)}
                          >
                            <span className="partner-name">{partner.name}</span>
                            {isProjectPartner && (
                              <span className="partner-role-badge">{partner.role || "Projektpartner"}</span>
                            )}
                            {!isProjectPartner && (
                              <span className="partner-crm-badge">CRM</span>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                  {showPartnerSuggestions && formData.partner_name.length >= 2 && partnerSuggestions.length === 0 && (
                    <div className="partner-suggestions">
                      <div
                        className="partner-suggestion-item create-new"
                        onClick={handleCreateNewPartner}
                      >
                        <span>+ "{formData.partner_name}" als neuen Partner erstellen</span>
                      </div>
                    </div>
                  )}
                  {formData.partner_name && !formData.partner_id && partnerSuggestions.length > 0 && !showPartnerSuggestions && (
                    <div className="partner-hint">
                      Partner nicht verknüpft. Wählen Sie aus der Liste oder erstellen Sie einen neuen.
                    </div>
                  )}
                </div>
              )}

              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    name="ist_prognose"
                    checked={formData.ist_prognose}
                    onChange={handleInputChange}
                  />
                  Prognose
                </label>
              </div>
            </div>

            <div className="form-actions">
              <div style={{ display: "flex", gap: "0.5rem" }}>
                <button
                  type="button"
                  onClick={handleCancel}
                  className="btn btn-secondary"
                  disabled={loading}
                >
                  {readOnly ? 'Schliessen' : 'Abbrechen'}
                </button>
                {!readOnly && editingEntry && (
                  <button
                    type="button"
                    onClick={() => {
                      if (window.confirm("Möchten Sie diesen Eintrag wirklich löschen?")) {
                        handleDelete(editingEntry.id);
                      }
                    }}
                    className="btn btn-danger"
                    disabled={loading}
                  >
                    Löschen
                  </button>
                )}
              </div>
              {!readOnly && (
                <button
                  type="button"
                  onClick={handleSubmit}
                  className="btn btn-primary"
                  disabled={loading}
                >
                  {loading
                    ? "Speichere..."
                    : editingEntry
                    ? "Aktualisieren"
                    : "Speichern"}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Control Panel: regelmässige Ausgaben als Karten, gruppiert */}
      <div className="budget-cards-container">
        {loading && !showForm && (
          <div className="loading-overlay">Lade Budget-Daten...</div>
        )}

        {/* Cashflow-Warnschwelle + Ø-Monatsausgaben (immer sichtbar) */}
        <section className="budget-warnschwelle">
          <div className="budget-warnschwelle__info">
            <span>
              Ø regelmässige Monatsausgaben (inkl. 13. Monatslohn):{" "}
              <strong>{formatCurrency(avgMonthlyExpenses)}</strong>
            </span>
            <span>
              Standard-Warnschwelle (3 × Monatsausgaben):{" "}
              <strong>{formatCurrency(defaultWarnschwelle)}</strong>
            </span>
            <span>
              Aktive Warnschwelle im Cashflow-Graph:{" "}
              <strong>{formatCurrency(effektiveWarnschwelle)}</strong>{" "}
              {warnschwelle == null ? "(Standard)" : "(fix gesetzt)"}
            </span>
          </div>
          {!readOnly && (
            <div className="budget-warnschwelle__form">
              <label>
                Warnschwelle (CHF)
                <input
                  type="number"
                  min="0"
                  step="100"
                  value={warnschwelleInput}
                  onChange={(e) => setWarnschwelleInput(e.target.value)}
                  placeholder={`Standard: ${defaultWarnschwelle}`}
                />
              </label>
              <button
                className="btn btn-primary btn-sm"
                onClick={handleSaveWarnschwelle}
                disabled={warnschwelleSaving || loading}
              >
                Speichern
              </button>
              <button
                className="btn btn-secondary btn-sm"
                onClick={handleResetWarnschwelle}
                disabled={warnschwelleSaving || loading || warnschwelle == null}
              >
                Auf Standard zurücksetzen
              </button>
            </div>
          )}
        </section>

        <section className="budget-card-group">
          <h3 className="budget-card-group__title">Regelmässige Ausgaben</h3>
          {recurringCards.allgemein.length === 0 && !loading ? (
            <div className="empty-state">Keine regelmässigen Ausgaben erfasst</div>
          ) : (
            <div className="budget-card-grid">
              {recurringCards.allgemein.map((entry) => renderExpenseCard(entry))}
            </div>
          )}
        </section>

        <section className="budget-card-group">
          <h3 className="budget-card-group__title">Löhne &amp; Sozialabgaben</h3>
          <p className="budget-card-group__hint">
            Diese Einträge werden über das Team-Profil bzw. die
            Versicherungssätze gesteuert; 13. Monatslöhne laufen automatisch mit.
          </p>
          {recurringCards.personal.length === 0 && !loading ? (
            <div className="empty-state">Keine Lohn-Einträge vorhanden</div>
          ) : (
            <div className="budget-card-grid">
              {recurringCards.personal.map((entry) => renderExpenseCard(entry))}
            </div>
          )}
        </section>
      </div>

      {/* Team-Profil (Lohn-Karten) */}
      {teamModalMember && (
        <TeamDetailModal
          member={teamModalMember}
          user={null}
          canManageTeam={true}
          onClose={handleTeamModalClose}
        />
      )}

      {/* Versicherungssätze (Sozialabgaben-/FAK-Karten) */}
      {showInsuranceModal && (
        <InsuranceSettingsModal
          onClose={() => setShowInsuranceModal(false)}
          onRatesChanged={handleRatesChanged}
        />
      )}

      {/* Partner Role Modal */}
      {showPartnerRoleModal && pendingPartner && (
        <div className="partner-role-overlay" onClick={() => { setShowPartnerRoleModal(false); setPendingPartner(null); }}>
          <div className="partner-role-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Partner mit Projekt verknüpfen</h3>
            <div className="partner-role-info">
              <p>
                <strong>{pendingPartner.name}</strong> wird mit dem Projekt verknüpft.
              </p>
            </div>
            <div className="form-group">
              <label>Rolle im Projekt</label>
              <input
                type="text"
                placeholder="z.B. Lieferant, Berater, Subunternehmer..."
                id="partner-role-input"
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === "Enter" && e.target.value.trim()) {
                    handlePartnerRoleConfirm(e.target.value.trim());
                  }
                }}
              />
            </div>
            <div className="form-actions">
              <button
                onClick={() => {
                  setShowPartnerRoleModal(false);
                  setPendingPartner(null);
                }}
                className="btn btn-secondary"
              >
                Abbrechen
              </button>
              <button
                onClick={() => {
                  const input = document.getElementById("partner-role-input");
                  if (input && input.value.trim()) {
                    handlePartnerRoleConfirm(input.value.trim());
                  }
                }}
                className="btn btn-primary"
              >
                Verknüpfen
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BudgetVerwaltungMwSt;
